/**
 * CommissionDetailsPage — Issue #372.
 *
 * Per-role commission detail list with filters + inline freeze / unfreeze /
 * clawback / reversal actions. Sensitive money columns are derived from the
 * decimal string the API returns; no JS number arithmetic on the frontend.
 */
import { useEffect, useState } from 'react';
import Decimal from 'decimal.js';
import { useAuth } from '@/hooks/useAuth';
import {
  FINANCE_PERMISSIONS,
  hasPermission,
} from '@/lib/financePermissions';
import {
  useCommissionDetails,
  useCommissionEvents,
  type CommissionDetail,
} from '@/hooks/useCommission372';

const RELEASE_LABELS: Record<string, string> = {
  pending_unlock: '待解锁',
  released: '已释放',
  frozen: '已冻结',
  clawback_pending: '冲回待执行',
  clawed_back: '已冲回',
  reversed: '已反向',
};

const PAYOUT_LABELS: Record<string, string> = {
  not_eligible: '未触发',
  payout_ready: '可发放',
  payout_linked: '已挂账',
  paid: '已发放',
  payout_blocked: '已 HR 拦截',
};

const ROLE_LABELS: Record<string, string> = {
  development: '开发',
  follow_up: '跟进',
  lead_close: '主导压单',
  assistant_close: '辅助压单',
  base_service: '基础服务',
  solution_writing: '方案撰写',
};

function fmtCny(amount: string): string {
  try {
    return `¥${new Decimal(amount).toFixed(2)}`;
  } catch {
    return amount;
  }
}
function fmtPct(rate: string): string {
  try {
    return `${new Decimal(rate).mul(100).toFixed(2)}%`;
  } catch {
    return rate;
  }
}

export default function CommissionDetailsPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(
    permissions,
    FINANCE_PERMISSIONS.COMMISSION_DETAIL_READ,
  );
  const canFreeze = hasPermission(
    permissions,
    FINANCE_PERMISSIONS.COMMISSION_DETAIL_FREEZE,
  );
  const canClawback = hasPermission(
    permissions,
    FINANCE_PERMISSIONS.COMMISSION_DETAIL_CLAWBACK,
  );
  const canReadEvents = hasPermission(
    permissions,
    FINANCE_PERMISSIONS.COMMISSION_EVENT_READ,
  );

  const {
    details,
    loading,
    error,
    fetchDetails,
    freezeDetail,
    unfreezeDetail,
    requestClawback,
    executeClawback,
    reverseDetail,
  } = useCommissionDetails();
  const { events, fetchEvents } = useCommissionEvents();

  const [filterProjectId, setFilterProjectId] = useState('');
  const [filterEmployeeId, setFilterEmployeeId] = useState('');
  const [filterReleaseStatus, setFilterReleaseStatus] = useState<
    NonNullable<CommissionDetail['releaseStatus']> | ''
  >('');
  const [openEventsId, setOpenEventsId] = useState<string | null>(null);

  useEffect(() => {
    if (canRead) {
      void fetchDetails({
        orderProjectId: filterProjectId || undefined,
        employeeId: filterEmployeeId || undefined,
        releaseStatus:
          filterReleaseStatus === ''
            ? undefined
            : (filterReleaseStatus as
                | 'pending_unlock'
                | 'released'
                | 'frozen'
                | 'clawback_pending'
                | 'clawed_back'
                | 'reversed'),
      });
    }
  }, [canRead, fetchDetails, filterProjectId, filterEmployeeId, filterReleaseStatus]);

  if (!canRead) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-muted-foreground">
        您没有查看提成明细的权限。
      </div>
    );
  }

  async function handleFreeze(d: CommissionDetail) {
    const reason = prompt('冻结原因（必填）：');
    if (!reason) return;
    const updated = await freezeDetail(d.id, {
      expectedRecordVersion: d.recordVersion,
      reason,
    });
    if (updated) void fetchDetails();
  }

  async function handleUnfreeze(d: CommissionDetail) {
    const reason = prompt('解冻原因（可空）：') ?? '';
    const updated = await unfreezeDetail(d.id, {
      expectedRecordVersion: d.recordVersion,
      reason: reason || null,
    });
    if (updated) void fetchDetails();
  }

  // Step 1: released | frozen -> clawback_pending. The backend state
  // machine refuses to jump from released/frozen directly to clawed_back,
  // so the UI must request the pending state first.
  async function handleRequestClawback(d: CommissionDetail) {
    const reason = prompt('申请冲回原因（必填）：');
    if (!reason) return;
    const amount = prompt('计划冲回金额（decimal）：');
    if (!amount) return;
    const updated = await requestClawback(d.id, {
      expectedRecordVersion: d.recordVersion,
      amountDecimal: amount,
      reason,
    });
    if (updated) void fetchDetails();
  }

  // Step 2: clawback_pending -> clawed_back. Only valid once a request
  // has moved the detail into the pending state above.
  async function handleExecuteClawback(d: CommissionDetail) {
    const reason = prompt('执行冲回原因（必填）：');
    if (!reason) return;
    const amount = prompt('实际冲回金额（decimal）：');
    if (!amount) return;
    const updated = await executeClawback(d.id, {
      expectedRecordVersion: d.recordVersion,
      amountDecimal: amount,
      reason,
    });
    if (updated) void fetchDetails();
  }

  async function handleReverse(d: CommissionDetail) {
    const reason = prompt('反向调整原因（必填）：');
    if (!reason) return;
    const amount = prompt('调整金额（decimal）：');
    if (!amount) return;
    const updated = await reverseDetail(d.id, {
      expectedRecordVersion: d.recordVersion,
      amountDecimal: amount,
      reason,
    });
    if (updated) void fetchDetails();
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">提成明细 (#372)</h1>
      </div>
      <div className="flex flex-wrap gap-3">
        <input
          className="rounded border border-gray-300 px-3 py-1.5 text-sm"
          placeholder="订单项目 ID"
          value={filterProjectId}
          onChange={(e) => setFilterProjectId(e.target.value)}
        />
        <input
          className="rounded border border-gray-300 px-3 py-1.5 text-sm"
          placeholder="员工 ID"
          value={filterEmployeeId}
          onChange={(e) => setFilterEmployeeId(e.target.value)}
        />
        <select
          className="rounded border border-gray-300 px-3 py-1.5 text-sm"
          value={filterReleaseStatus}
          onChange={(e) =>
            setFilterReleaseStatus(
              e.target.value as
                | NonNullable<CommissionDetail['releaseStatus']>
                | '',
            )
          }
        >
          <option value="">全部释放状态</option>
          {Object.entries(RELEASE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="rounded bg-red-50 px-4 py-3 text-sm text-red-700">
          {error.message}
        </div>
      )}
      {loading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          加载中…
        </div>
      ) : (
        <div className="overflow-x-auto rounded border bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-gray-500">项目</th>
                <th className="px-3 py-2 text-left text-gray-500">角色</th>
                <th className="px-3 py-2 text-left text-gray-500">员工</th>
                <th className="px-3 py-2 text-left text-gray-500">基数</th>
                <th className="px-3 py-2 text-left text-gray-500">比例</th>
                <th className="px-3 py-2 text-left text-gray-500">提成</th>
                <th className="px-3 py-2 text-left text-gray-500">释放</th>
                <th className="px-3 py-2 text-left text-gray-500">发放</th>
                <th className="px-3 py-2 text-right text-gray-500">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {details.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    className="px-3 py-6 text-center text-muted-foreground"
                  >
                    暂无明细
                  </td>
                </tr>
              ) : (
                details.map((d) => (
                  <>
                    <tr key={d.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono text-xs">
                        {d.orderProjectId.slice(0, 8)}…
                      </td>
                      <td className="px-3 py-2">
                        {ROLE_LABELS[d.roleType] ?? d.roleType}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">
                        {d.employeeId.slice(0, 8)}…
                      </td>
                      <td className="px-3 py-2">
                        {fmtCny(d.baseAmountDecimal)}
                      </td>
                      <td className="px-3 py-2">{fmtPct(d.rateDecimal)}</td>
                      <td className="px-3 py-2 font-semibold">
                        {fmtCny(d.commissionAmountDecimal)}
                      </td>
                      <td className="px-3 py-2">
                        {RELEASE_LABELS[d.releaseStatus] ?? d.releaseStatus}
                      </td>
                      <td className="px-3 py-2">
                        {PAYOUT_LABELS[d.payoutStatus] ?? d.payoutStatus}
                      </td>
                      <td className="px-3 py-2 text-right space-x-1">
                        {canFreeze && d.releaseStatus === 'released' && (
                          <button
                            className="rounded bg-yellow-500 px-2 py-0.5 text-xs text-white"
                            onClick={() => {
                              void handleFreeze(d);
                            }}
                          >
                            冻结
                          </button>
                        )}
                        {canFreeze && d.releaseStatus === 'frozen' && (
                          <button
                            className="rounded bg-blue-500 px-2 py-0.5 text-xs text-white"
                            onClick={() => {
                              void handleUnfreeze(d);
                            }}
                          >
                            解冻
                          </button>
                        )}
                        {canClawback &&
                          (d.releaseStatus === 'released' ||
                            d.releaseStatus === 'frozen') && (
                            <button
                              className="rounded bg-red-400 px-2 py-0.5 text-xs text-white"
                              onClick={() => {
                                void handleRequestClawback(d);
                              }}
                            >
                              申请冲回
                            </button>
                          )}
                        {canClawback &&
                          d.releaseStatus === 'clawback_pending' && (
                            <button
                              className="rounded bg-red-600 px-2 py-0.5 text-xs text-white"
                              onClick={() => {
                                void handleExecuteClawback(d);
                              }}
                            >
                              执行冲回
                            </button>
                          )}
                        {canClawback &&
                          d.releaseStatus !== 'reversed' &&
                          d.releaseStatus !== 'clawed_back' &&
                          d.releaseStatus !== 'clawback_pending' && (
                            <button
                              className="rounded bg-purple-500 px-2 py-0.5 text-xs text-white"
                              onClick={() => {
                                void handleReverse(d);
                              }}
                            >
                              反向
                            </button>
                          )}
                        {canReadEvents && (
                          <button
                            className="rounded bg-gray-200 px-2 py-0.5 text-xs"
                            onClick={() => {
                              const next = openEventsId === d.id ? null : d.id;
                              setOpenEventsId(next);
                              if (next) void fetchEvents(d.id);
                            }}
                          >
                            事件
                          </button>
                        )}
                      </td>
                    </tr>
                    {openEventsId === d.id && (
                      <tr>
                        <td
                          colSpan={9}
                          className="bg-gray-50 px-3 py-2 text-xs text-gray-700"
                        >
                          {events.length === 0 ? (
                            <div>暂无事件</div>
                          ) : (
                            <ul className="space-y-1">
                              {events.map((e) => (
                                <li key={e.id}>
                                  <span className="font-mono">
                                    {e.createdAt.slice(0, 19).replace('T', ' ')}
                                  </span>{' '}
                                  {e.action}{' '}
                                  {e.fromReleaseStatus && e.toReleaseStatus
                                    ? `[${e.fromReleaseStatus} → ${e.toReleaseStatus}]`
                                    : ''}{' '}
                                  {e.reason ? `(${e.reason})` : ''}
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
